/**
 * Single source of truth for Disney experience availability (rides, shows, etc.).
 * Evaluates closure, refurbishment, opening dates, and explicit disabled state
 * so all selectors, validation, and submission use the same rules.
 *
 * Rules:
 * - closurePatterns / closureNoteKeywords / note: closed for trip if trip start >= date
 * - openingPatterns / openingNoteKeywords: disabled until opening date (trip start >= date)
 * - "TBD" opening keyword: no date → always disabled (not open yet)
 * - Explicit option.disabled, option.isClosed, option.status (future)
 */

import { closurePatterns, parseKeywordToDate as parseClosureKeyword } from "../data/closurePatterns";
import { openingPatterns, parseKeywordToDate as parseOpeningKeyword } from "../data/openingPatterns";
import { rideOptionsByPark } from "../data/rideOptionsByPark";
import { showOptionsByPark } from "../data/showOptionsByPark";

const DEBUG_AVAILABILITY = typeof window !== "undefined" && window.__PARKPRO_DEBUG_AVAILABILITY__ === true;

/** ISO date regex for deterministic local-midnight parsing */
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse tripStartDate deterministically.
 * - "YYYY-MM-DD" → local midnight (new Date(y, m-1, d))
 * - Other valid Date.parse string → new Date(tripStartDate)
 * - Else null
 * @packageDocumentation exported for tests
 */
export function parseTripStartDate(tripStartDate) {
  if (tripStartDate == null) return null;
  if (tripStartDate instanceof Date) {
    return isNaN(tripStartDate.getTime()) ? null : tripStartDate;
  }
  if (typeof tripStartDate !== "string") return null;
  const s = tripStartDate.trim();
  if (ISO_DATE_ONLY.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    return isNaN(date.getTime()) ? null : date;
  }
  const parsed = Date.parse(tripStartDate);
  return Number.isNaN(parsed) ? null : new Date(parsed);
}

/** Normalize ordinal in keyword so date parser can match (e.g. "july 7th, 2025" → "july 7, 2025") */
function normalizeKeywordForDate(keyword) {
  if (typeof keyword !== "string") return "";
  return keyword
    .toLowerCase()
    .trim()
    .replace(/\b(\d+)(st|nd|rd|th)\b/gi, "$1");
}

/**
 * Get the "note" text used for closure/opening keyword matching.
 * Rides use `note`, shows use `showInfoNote`.
 */
function getNoteText(option, optionType) {
  if (!option) return "";
  if (optionType === "show") return (option.showInfoNote || option.note || "").toLowerCase();
  return (option.note || "").toLowerCase();
}

/**
 * Check if option is closed for the trip (refurbishment, permanent closure, etc.).
 * Uses closureNoteKeywords, note text, and closurePatterns.
 */
function isClosedForTrip(option, tripStart, optionType) {
  if (!option || !tripStart) return false;
  const normalizedNote = getNoteText(option, optionType);
  const keywords = option.closureNoteKeywords || [];

  for (const kw of keywords) {
    const normalizedKw = normalizeKeywordForDate(kw);
    const patternMatch = closurePatterns.find(
      (p) => normalizedKw === p.keyword
    );
    if (patternMatch && tripStart >= patternMatch.date) return true;
    const parsed = parseClosureKeyword(normalizedKw);
    if (parsed && tripStart >= parsed) return true;
  }

  for (const { keyword, date } of closurePatterns) {
    if (normalizedNote.includes(keyword) && tripStart >= date) return true;
  }

  return false;
}

/**
 * Check if option is not yet open for the trip (openingNoteKeywords).
 * Returns true if the option should be DISABLED (not open yet).
 * "TBD" = no known date → always disabled.
 */
function isOpeningNotYetForTrip(option, tripStart, optionType) {
  if (!option || !tripStart) return false;
  const keywords = option.openingNoteKeywords || [];
  if (keywords.length === 0) return false;

  const normalizedNote = getNoteText(option, optionType);

  for (const kw of keywords) {
    const lower = (kw || "").toLowerCase().trim();
    if (lower === "tbd") return true;

    const normalizedKw = normalizeKeywordForDate(kw);
    const patternMatch = openingPatterns.find(
      (p) => normalizedKw === p.keyword
    );
    if (patternMatch && tripStart < patternMatch.date) return true;
    const parsed = parseOpeningKeyword(normalizedKw);
    if (parsed && tripStart < parsed) return true;
  }

  for (const { keyword, date } of openingPatterns) {
    if (normalizedNote.includes(keyword) && tripStart < date) return true;
  }

  return false;
}

/**
 * Single source of truth: is this option available for selection?
 * @param {Object} option - Ride or show option (name, note, closureNoteKeywords, openingNoteKeywords, etc.)
 * @param {Object} context - { tripStartDate: Date|string|null, optionType: 'ride'|'show' }
 * @returns {{ available: boolean, reason?: string, rule?: string }}
 */
export function getOptionAvailability(option, context) {
  const { tripStartDate, optionType = "ride" } = context || {};
  const tripStart = parseTripStartDate(tripStartDate);

  if (!option) {
    return { available: false, reason: "Missing option", rule: "missing" };
  }

  if (option.disabled === true) {
    return { available: false, reason: option.tooltip || "Currently unavailable", rule: "disabled" };
  }

  if (option.isClosed === true) {
    return { available: false, reason: "Closed", rule: "isClosed" };
  }

  if (!tripStart) {
    return { available: true };
  }

  const closed = isClosedForTrip(option, tripStart, optionType);
  if (closed) {
    const reason = "May be closed for your trip dates (refurbishment or closure).";
    if (DEBUG_AVAILABILITY) {
      console.debug("[availabilityRules] closed:", option.name, { tripStart: tripStart.toISOString(), optionType });
    }
    return { available: false, reason, rule: "closure" };
  }

  const notYetOpen = isOpeningNotYetForTrip(option, tripStart, optionType);
  if (notYetOpen) {
    const reason =
      option.openingNoteKeywords?.some((k) => (k || "").toLowerCase().trim() === "tbd")
        ? "Opening date TBD."
        : "Not open yet for your trip dates.";
    if (DEBUG_AVAILABILITY) {
      console.debug("[availabilityRules] opening not yet:", option.name, { tripStart: tripStart.toISOString(), optionType });
    }
    return { available: false, reason, rule: "opening" };
  }

  return { available: true };
}

/**
 * Filter selected ride names to only those available for the current form context.
 * Used by RideSelector useEffect, validation, and sanitization.
 */
export function filterAvailableRides(selectedRideNames, formData) {
  const tripStart =
    formData?.basicInformation?.startDate && !isNaN(new Date(formData.basicInformation.startDate).getTime())
      ? new Date(formData.basicInformation.startDate)
      : null;
  const parks = formData?.parkVisitPlans?.parks || [];
  if (!Array.isArray(selectedRideNames)) return { filtered: [], removed: [] };

  const allRides = parks.flatMap((park) => rideOptionsByPark[park] || []);
  const nameToOption = new Map(allRides.map((r) => [r.name, r]));

  const filtered = [];
  const removed = [];
  for (const name of selectedRideNames) {
    const option = nameToOption.get(name);
    const ctx = { tripStartDate: tripStart, optionType: "ride" };
    const { available } = getOptionAvailability(option || { name }, ctx);
    if (available) filtered.push(name);
    else removed.push(name);
  }

  if (DEBUG_AVAILABILITY && removed.length > 0) {
    console.debug("[availabilityRules] filterAvailableRides removed:", removed, { tripStart: tripStart?.toISOString() });
  }
  return { filtered, removed };
}

/**
 * Filter selected show names to only those available for the current form context.
 */
export function filterAvailableShows(selectedShowNames, formData) {
  const tripStart =
    formData?.basicInformation?.startDate && !isNaN(new Date(formData.basicInformation.startDate).getTime())
      ? new Date(formData.basicInformation.startDate)
      : null;
  const parks = formData?.parkVisitPlans?.parks || [];

  if (!Array.isArray(selectedShowNames)) return { filtered: [], removed: [] };

  const allShows = parks.flatMap((park) => showOptionsByPark[park] || []);
  const nameToOption = new Map(allShows.map((s) => [s.name, s]));

  const filtered = [];
  const removed = [];
  for (const name of selectedShowNames) {
    const option = nameToOption.get(name);
    const ctx = { tripStartDate: tripStart, optionType: "show" };
    const { available } = getOptionAvailability(option || { name }, ctx);
    if (available) filtered.push(name);
    else removed.push(name);
  }

  if (DEBUG_AVAILABILITY && removed.length > 0) {
    console.debug("[availabilityRules] filterAvailableShows removed:", removed, { tripStart: tripStart?.toISOString() });
  }
  return { filtered, removed };
}

/**
 * Normalize a section option (string or object) for getOptionAvailability.
 * Section options use value/label; availability logic expects name for diagnostics.
 */
function normalizeSectionOption(option) {
  if (option == null) return null;
  if (typeof option === "string") {
    return { name: option, value: option, label: option };
  }
  return {
    ...option,
    name: option.name ?? option.value ?? option.label,
  };
}

/**
 * Filter selected section option values (e.g. parks) to only those available
 * for the current form context (tripStartDate). Used by parks multi-select,
 * validation, and sanitization.
 * @param {string[]} selectedValues - Currently selected values (e.g. park names)
 * @param {Array<string|Object>} options - Field options (strings or { value, label, ... })
 * @param {Object} formData - Form data with basicInformation.startDate
 * @returns {{ filtered: string[], removed: string[] }}
 */
export function filterAvailableSectionOptions(selectedValues, options, formData) {
  const tripStart =
    formData?.basicInformation?.startDate && !isNaN(new Date(formData.basicInformation.startDate).getTime())
      ? new Date(formData.basicInformation.startDate)
      : null;
  if (!Array.isArray(selectedValues)) return { filtered: [], removed: [] };
  const opts = Array.isArray(options) ? options : [];
  const valueToOption = new Map(
    opts.map((o) => {
      const val = typeof o === "object" ? o.value : o;
      return [val, normalizeSectionOption(o)];
    })
  );

  const filtered = [];
  const removed = [];
  for (const val of selectedValues) {
    const option = valueToOption.get(val) || normalizeSectionOption({ value: val, label: val });
    const ctx = { tripStartDate: tripStart, optionType: "sectionOption" };
    const { available } = getOptionAvailability(option, ctx);
    if (available) filtered.push(val);
    else removed.push(val);
  }

  if (DEBUG_AVAILABILITY && removed.length > 0) {
    console.debug("[availabilityRules] filterAvailableSectionOptions removed:", removed, { tripStart: tripStart?.toISOString() });
  }
  return { filtered, removed };
}

/**
 * Get availability for a section option (for rendering). Use when the option
 * has closureNoteKeywords, openingNoteKeywords, note, or disabled.
 */
export function getSectionOptionAvailability(option, tripStartDate) {
  const normalized = normalizeSectionOption(option);
  return getOptionAvailability(normalized, { tripStartDate, optionType: "sectionOption" });
}

/**
 * For dev/diagnostics: which options are disabled and why.
 * Set window.__PARKPRO_DEBUG_AVAILABILITY__ = true to enable console logs.
 */
export function getAvailabilityDiagnostics(formData, optionType = "ride") {
  const tripStart =
    formData?.basicInformation?.startDate && !isNaN(new Date(formData.basicInformation.startDate).getTime())
      ? new Date(formData.basicInformation.startDate)
      : null;
  const parks = formData?.parkVisitPlans?.parks || [];
  const optionsByPark = optionType === "ride" ? rideOptionsByPark : showOptionsByPark;
  const allOptions = parks.flatMap((park) => (optionsByPark[park] || []).map((o) => ({ ...o, _park: park })));

  return allOptions.map((option) => {
    const result = getOptionAvailability(option, { tripStartDate: tripStart, optionType });
    return {
      name: option.name,
      park: option._park,
      ...result,
      dateWindow: option.openingNoteKeywords?.length
        ? `opening: ${(option.openingNoteKeywords || []).join(", ")}`
        : option.closureNoteKeywords?.length
          ? `closure: ${(option.closureNoteKeywords || []).join(", ")}`
          : null,
    };
  });
}
